import React, { useState, useEffect } from "react";
import { Col, Container, Row, Alert, Button } from "react-bootstrap";

// router
import { useParams, useHistory } from "react-router-dom";

// firebase
import { db } from "../../firebase";

import { CustomSpinner } from "../shared/Spinner";
import Header from "../header/header";

import { PreviewDetails } from "./preview-details";

export const PlayerPreviewComponents = () => {
  const history = useHistory();
  const { id, compareId } = useParams();
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Route changes reuse this component. Ignore requests from the previous route.
    let cancelled = false;
    setResult(null);

    const load = async () => {
      try {
        const [player, comparison] = await Promise.all([
          loadPlayerPreview(id),
          compareId ? loadPlayerPreview(compareId) : Promise.resolve(null),
        ]);
        if (!cancelled) setResult({ id, compareId, player, comparison });
      } catch (error) {
        console.error("Unable to load player preview", error);
        if (!cancelled) setResult({ id, compareId, error: true });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id, compareId]);

  // Hide the previous player's content even before the new effect runs.
  if (!result || result.id !== id || result.compareId !== compareId) {
    return <CustomSpinner />;
  }
  if (result.error) {
    return <Alert variant="danger">Unable to load player profile. Please try again.</Alert>;
  }
  if (!result.player.profile) {
    return <Alert variant="warning">Player profile not found.</Alert>;
  }

  const { profile: playerProfileData, background: bgOverviewData, scouting: scoutingOverviewData } = result.player;
  const compareProfile = result.comparison?.profile;
  const compareBgOverview = result.comparison?.background;
  const compareScoutingOverview = result.comparison?.scouting;
  const isCompareActive = Boolean(compareProfile);

  return (
    <>
      <Header />

      <Alert show={isCompareActive} variant="danger">
        <div className="w-100 d-flex justify-content-between">
          <p>Close Compare window.</p>
          <Button
            onClick={() => {
              history.push(`/player-profile/preview/${id}`);
            }}
            variant="outline-danger"
          >
            X
          </Button>
        </div>
      </Alert>
      <Container fluid>
        <Row className="g-3 g-xxl-0">
          <Col
            xs={12}
            xxl={isCompareActive ? 6 : 12}
            className={isCompareActive ? "px-0 px-xxl-2" : "p-0"}
          >
            <PreviewDetails
              key={id}
              compare={isCompareActive}
              playerProfileData={playerProfileData}
              bgOverviewData={bgOverviewData}
              scoutingOverviewData={scoutingOverviewData}
              id={id}
            />
          </Col>
          {compareProfile && (
            <Col xs={12} xxl={6} className="px-0 px-xxl-2">
              <PreviewDetails
                key={compareId}
                compare
                playerProfileData={compareProfile}
                bgOverviewData={compareBgOverview}
                scoutingOverviewData={compareScoutingOverview}
                id={compareId}
              />
            </Col>
          )}{" "}
        </Row>
      </Container>
    </>
  );
};

// Publish one complete bundle, including explicit nulls for missing overviews.
const loadPlayerPreview = async (playerId) => {
  const [profileDoc, backgroundDocs, scoutingDocs] = await Promise.all([
    db.collection("player-profile").doc(playerId).get(),
    db.collection("background-overview").where("playerProfile", "==", playerId).get(),
    db.collection("scouting-overview").where("playerProfile", "==", playerId).get(),
  ]);
  const scouting = scoutingDocs.docs[0]?.data();
  return {
    profile: profileDoc.data() ?? null,
    background: backgroundDocs.docs[0]?.data() ?? null,
    scouting: scouting ? getScoutingOverviewDetails(scouting) : null,
  };
};

const getScoutingOverviewDetails = (scoutingOverviewData) => {
  let data = {
    strengths: {
      Strengths: {
        list: scoutingOverviewData.strengths ?? [],
      },
    },
    projections: {
      Projections: {
        title: "Projections",
        list: [],
      },
    },
    weaknesses: {
      Weaknesses: {
        list: scoutingOverviewData.weaknesses ?? [],
      },
    },
    questions: {
      Questions: {
        list: scoutingOverviewData.questions ?? [],
      },
    },
    roles: {
      Roles: {
        title: "Roles",
        list: scoutingOverviewData.roles ?? [],
      },
    },
    bottomLine: scoutingOverviewData.bottomLine ?? "",
  };
  scoutingOverviewData.collegeLevel &&
    data.projections.Projections.list.push(
      `College Level: ${scoutingOverviewData.collegeLevel}`
    );
  scoutingOverviewData.collegeCeiling &&
    data.projections.Projections.list.push(
      `College Ceiling: ${scoutingOverviewData.collegeCeiling}`
    );
  scoutingOverviewData.nbaDraft &&
    data.projections.Projections.list.push(
      `NBA Draft: ${scoutingOverviewData.nbaDraft}`
    );
  scoutingOverviewData.nbaCeiling &&
    data.projections.Projections.list.push(
      `NBA Ceiling: ${scoutingOverviewData.nbaCeiling}`
    );

  return data;
};
